import { useCallback, useEffect, useMemo } from 'react';

import { shuffle } from 'lodash';

import { legacyConvertCurrency } from '@pkg/components/deprecated';
import { useAsyncGetCompetitionCollectionsMetadata, useGamesContent, useGetCurrencyConversion } from '@pkg/hooks';

import { Currency } from '@app/client';
import { type FantasyLandingCollectionItemProps } from '@app/pages/FantasyLanding/Hero/HeroCollections/types';
import { mocData } from '@app/pages/FantasyWishlist/components/CollectionTable/data';
import { useFantasyLandingAutoGeneratedLineupStore } from '@app/store/zustand/FantasyLanding/AutoGeneratedLineup';

export const useProdFantasyItems = (disabled?: boolean) => {
    const getFloorPrice = (item: FantasyLandingCollectionItemProps) =>
        legacyConvertCurrency(parseFloat(item?.floor_price_wei || '0'), Currency.WEI, Currency.ETH) || 0;

    const { autoGeneratedLineup, setAutoGeneratedLineup, setActiveGame, activeRedirect, activeGame } =
        useFantasyLandingAutoGeneratedLineupStore();
    const { data: currencyConversionData, isLoading: isLoadingConversionRate } = useGetCurrencyConversion({
        interval: false,
        disabled,
    });
    const conversionRate = useMemo(
        () => currencyConversionData?.ethereum?.usd || 1,
        [currencyConversionData?.ethereum?.usd],
    );

    const { activeGames: gamesSortedBySubmissionEnds, activeLoading: isLoadingGames } = useGamesContent({});

    const now = new Date();
    const activeGameData =
        gamesSortedBySubmissionEnds?.find((game) => {
            return !game.wishlists?.length && new Date(game?.submission_ends_at || '') > now;
        }) || gamesSortedBySubmissionEnds?.[0];

    const minAssets = 3;

    const maxCollectionFloorPrice = useMemo(
        () => (activeGameData?.max_value || 100000) / minAssets,
        [activeGameData?.max_value],
    );

    const filteredItems = mocData.filter((item) => getFloorPrice(item) * conversionRate <= maxCollectionFloorPrice);

    const withQuantity = useCallback(
        (items: FantasyLandingCollectionItemProps[]) => {
            return items.map((item) => {
                return {
                    ...item,
                    quantity: Math.floor(maxCollectionFloorPrice / (getFloorPrice(item) * conversionRate)),
                };
            });
        },
        [conversionRate, maxCollectionFloorPrice],
    );
    // TODO add chains
    const fetchCollectionsMetadata = useAsyncGetCompetitionCollectionsMetadata({ setMetadataStore: true });
    const confirmFloorPrices = useCallback(
        (items: FantasyLandingCollectionItemProps[]) => {
            fetchCollectionsMetadata(items.map((item) => `ethereum.${item.slug}`)).then((r) => {
                if (r?.ok && r.value) {
                    const fetchedItems = r?.value.ethereum;
                    const needUpdate = fetchedItems?.find((fetchedItem) => {
                        const itemToCheck = items.find((item) => item.slug === fetchedItem.slug);
                        return itemToCheck?.floor_price_wei !== fetchedItem?.floor_price_wei;
                    });
                    if (needUpdate) {
                        const newItems = items.map((currItem) => {
                            const newItem = fetchedItems?.find((fetchedItem) => fetchedItem.slug === currItem.slug);
                            if (newItem) return newItem;
                            return currItem;
                        });
                        setAutoGeneratedLineup(withQuantity(newItems));
                    } else {
                        setAutoGeneratedLineup(withQuantity(items));
                    }
                }
            });
        },
        [fetchCollectionsMetadata, setAutoGeneratedLineup, withQuantity],
    );

    useEffect(() => {
        if (activeGameData) {
            if (activeGame?.slug !== activeGameData.slug) {
                setActiveGame(activeGameData);
            }
            if (conversionRate > 1 && !autoGeneratedLineup?.length) {
                const recommendedItems = shuffle(filteredItems).slice(0, minAssets);
                confirmFloorPrices(recommendedItems);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeGameData, conversionRate, confirmFloorPrices]);

    const updateRandomItems = () => {
        if (activeRedirect) return;
        const uniqueItems = new Set(autoGeneratedLineup.map((item) => item.metadata?.name));
        const newRandomItems = shuffle(filteredItems.filter((item) => !uniqueItems.has(item.metadata?.name))).slice(
            0,
            minAssets,
        );
        setAutoGeneratedLineup(withQuantity(newRandomItems));
        confirmFloorPrices(newRandomItems);
    };

    const updateItem = (index: number) => {
        if (activeRedirect) return;
        const updatedItems = [...autoGeneratedLineup];
        const filteredData = filteredItems.filter(
            (item) => !autoGeneratedLineup.find((currItem) => currItem.slug === item.slug),
        );

        const randomIndex = Math.floor(Math.random() * filteredData.length);
        const newRandomItem = filteredData[randomIndex];

        if (newRandomItem) {
            updatedItems[index] = { ...newRandomItem, quantity: 0 };
            setAutoGeneratedLineup(withQuantity(updatedItems));
            confirmFloorPrices(updatedItems);
        }
    };

    const totalFloorPrice = useMemo(
        () => autoGeneratedLineup.reduce((sum, item) => sum + getFloorPrice(item) * (item?.quantity || 0), 0),
        [autoGeneratedLineup],
    );

    const totalPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(totalFloorPrice * (conversionRate || 0));

    return {
        totalPrice,
        conversionRate,
        maxValue: activeGameData?.max_value || 0,
        randomItems: autoGeneratedLineup,
        updateRandomItems,
        updateItem,
        isLoading: isLoadingGames || isLoadingConversionRate, // || isLoadingMetadata || isFetchingMetadata
    };
};
